import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="ui--upload-form"
//
// Coordena o estado do formulário durante o upload direto (Active Storage) dos
// anexos. O Active Storage envia os arquivos ao armazenamento antes de submeter
// o formulário; enquanto isso ocorre, este controller:
//   - troca o botão de envio para o estado "Enviando…" e o desabilita;
//   - impede o fechamento acidental do modal (ver ui--turbo-modal), sinalizando
//     o upload em andamento no elemento raiz do documento;
//   - avisa antes de sair da página, evitando perder um envio em curso;
//   - reporta ao servidor os uploads que falharam (ver beaconUrl).
//
// Os eventos agregados `direct-uploads:start`/`direct-uploads:end` são
// disparados no próprio formulário pelo Active Storage.
export default class extends Controller {
  static targets = ["submit"]

  static values = {
    flag: { type: String, default: "data-upload-in-progress" },
    beaconUrl: String
  }

  connect() {
    this.boundStart = this.start.bind(this)
    this.boundEnd = this.end.bind(this)
    this.boundError = this.error.bind(this)
    this.boundWarn = this.warn.bind(this)

    this.element.addEventListener("direct-uploads:start", this.boundStart)
    this.element.addEventListener("direct-uploads:end", this.boundEnd)
    this.element.addEventListener("direct-upload:error", this.boundError)
  }

  disconnect() {
    this.element.removeEventListener("direct-uploads:start", this.boundStart)
    this.element.removeEventListener("direct-uploads:end", this.boundEnd)
    this.element.removeEventListener("direct-upload:error", this.boundError)
    this.release()
  }

  start() {
    this.failed = false
    document.documentElement.setAttribute(this.flagValue, "true")
    window.addEventListener("beforeunload", this.boundWarn)
    this.markSubmitting()
  }

  // Dispara ao concluir todos os uploads. Em caso de sucesso o formulário será
  // submetido em seguida, então o botão permanece desabilitado; em caso de erro,
  // o Active Storage cancela a submissão e restauramos o botão.
  end() {
    this.release()
    if (this.failed) this.restoreSubmit()
  }

  error(event) {
    this.failed = true
    this.report(event.detail)
  }

  // O PUT do upload direto vai do navegador direto ao storage: quando falha, o
  // servidor não vê nada — nem este formulário chega a ser submetido. Sem este
  // aviso a falha só apareceria depois, procurando blobs órfãos na mão.
  //
  // É best-effort: qualquer problema aqui é ignorado, porque o usuário já está
  // lidando com o upload que falhou.
  report({ file, error }) {
    if (!this.hasBeaconUrlValue) return

    post(this.beaconUrlValue, {
      body: {
        arquivo: file?.name,
        bytes: file?.size,
        erro: String(error),
        origem: window.location.pathname
      },
      responseKind: "json"
    }).catch(() => {})
  }

  release() {
    document.documentElement.removeAttribute(this.flagValue)
    window.removeEventListener("beforeunload", this.boundWarn)
  }

  warn(event) {
    event.preventDefault()
    event.returnValue = ""
  }

  markSubmitting() {
    if (!this.hasSubmitTarget) return

    this.originalSubmitHtml = this.submitTarget.innerHTML
    this.submitTarget.disabled = true
    this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
    this.submitTarget.textContent = "Enviando…"
  }

  restoreSubmit() {
    if (!this.hasSubmitTarget) return

    this.submitTarget.disabled = false
    this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
    if (this.originalSubmitHtml !== undefined) {
      this.submitTarget.innerHTML = this.originalSubmitHtml
    }
  }
}
